import { useState } from 'react';
import { CheckCircle2, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { PaginationControls } from '../../components/PaginationControls';
import { useVectorDbJobHistory } from '../hooks/useVectorDb';
import type { VectorDbSyncName } from '../types/vector-db.type';

const TH_BASE = 'px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD_BASE = 'px-4 py-3 whitespace-nowrap text-sm text-gray-900';
const DEFAULT_PAGE_SIZE = 10;

const TITLES: Record<VectorDbSyncName, string> = {
  providers: 'Providers Sync History',
  resources: 'Resources Sync History',
};

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const roundedSeconds = Math.round(seconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const rest = roundedSeconds % 60;
  return `${minutes}m ${rest}s`;
}

function StatusBadge({ status }: { status: string }) {
  // Care Engine returns this uppercase ("SUCCEEDED"/"FAILED") — normalize before matching.
  const normalized = status.toLowerCase();

  if (normalized === 'succeeded') {
    return (
      <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
        <CheckCircle2 className="h-3 w-3" /> Succeeded
      </Badge>
    );
  }
  if (normalized === 'failed') {
    return (
      <Badge variant="destructive">
        <XCircle className="h-3 w-3" /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">
      <Loader2 className="h-3 w-3 animate-spin" /> {status}
    </Badge>
  );
}

export function JobHistoryTable({ name }: { name: VectorDbSyncName }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // One API call per page — server returns only this page's rows + meta, same
  // page/pageSize contract as useAdminProviders.
  const history = useVectorDbJobHistory(name, { page, pageSize });
  const jobs = history.data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">{TITLES[name]}</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => history.refetch()}
          disabled={history.isFetching}
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${history.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-col border border-gray-200 rounded-md bg-white">
        {history.data && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm">
            <span>
              Total <span className="font-semibold text-gray-900">{history.data.meta.total}</span>{' '}
              {history.data.meta.total === 1 ? 'job' : 'jobs'}
            </span>
          </div>
        )}

        <div className="overflow-auto">
          {history.isLoading ? (
            <div className="flex items-center gap-1.5 text-gray-500 text-sm p-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading job history…
            </div>
          ) : history.isError ? (
            <div className="text-red-600 text-sm p-4">
              {history.error?.message || "We couldn't complete that action right now. Please try again."}
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-gray-500 text-sm p-4">No sync jobs have run yet.</div>
          ) : (
            <table className="w-full" role="table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className={TH_BASE}>Status</th>
                  <th className={TH_BASE}>Total</th>
                  <th className={TH_BASE}>Success</th>
                  <th className={TH_BASE}>Failed</th>
                  <th className={TH_BASE}>Started At</th>
                  <th className={TH_BASE}>Finished At</th>
                  <th className={TH_BASE}>Duration</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                    <td className={TD_BASE}>
                      <StatusBadge status={job.status} />
                      {job.status.toLowerCase() === 'failed' && job.error_message && (
                        <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={job.error_message}>
                          {job.error_message}
                        </div>
                      )}
                    </td>
                    <td className={TD_BASE}>{job.result?.total ?? '—'}</td>
                    <td className={TD_BASE}>{job.result?.success ?? '—'}</td>
                    <td className={TD_BASE}>{job.result?.failed ?? '—'}</td>
                    <td className={TD_BASE}>{formatTimestamp(job.started_at)}</td>
                    <td className={TD_BASE}>{formatTimestamp(job.finished_at)}</td>
                    <td className={TD_BASE}>{formatDuration(job.duration_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {history.data && history.data.meta.total > 0 && (
          <PaginationControls
            meta={history.data.meta}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>
    </div>
  );
}
